import type { BleConnectionError } from '../../interfaces/ble.interface';
import { BleConnectionErrorType } from '../../interfaces/ble.interface';

/**
 * Identify error type for better error handling.
 * Detects iOS and Android specific permission errors and prefixes them
 * with BLE_PERMISSION_DENIED so callers can distinguish permission issues.
 */
export function identifyBleErrorType(error: Error): string {
  const errorString = error.message || error.toString() || 'Unknown error';
  const lowerError = errorString.toLowerCase();

  const iosPermissionKeywords = [
    'not authorized',
    'unauthorized',
    'bluetooth permission',
    'permission denied',
    'user denied',
    'bluetooth is powered off',
    'bluetooth state unauthorized',
    'cbmanager',
  ];

  const androidPermissionKeywords = ['location permission', 'bluetooth_scan', 'bluetooth_connect', 'requires permission', 'missing permission'];

  if (iosPermissionKeywords.some(keyword => lowerError.includes(keyword))) {
    return `BLE_PERMISSION_DENIED: ${errorString}`;
  }

  if (androidPermissionKeywords.some(keyword => lowerError.includes(keyword))) {
    return `BLE_PERMISSION_DENIED: ${errorString}`;
  }

  return `Scan failed: ${errorString}`;
}

/**
 * Check if error message indicates a permission-related issue.
 * Relies on the BLE_PERMISSION_DENIED prefix added by identifyBleErrorType().
 */
export function isPermissionError(errorMessage: string): boolean {
  return errorMessage.startsWith('BLE_PERMISSION_DENIED:');
}

export function isLocationError(errorMessage: string): boolean {
  return errorMessage.toLowerCase().includes('location services are required');
}

export function classifyBleConnectionError(error: Error | unknown): BleConnectionError {
  let errorString: string;
  if (error instanceof Error) {
    errorString = error.message;
  } else if (typeof error === 'object' && error !== null && 'error' in error && typeof (error as { error: unknown }).error === 'string') {
    errorString = (error as { error: string }).error;
  } else {
    errorString = String(error);
  }
  const lowerError = errorString.toLowerCase();

  const bluetoothOffKeywords = ['bluetooth is powered off', 'bluetooth is not available', 'ble is not enabled'];
  if (bluetoothOffKeywords.some(keyword => lowerError.includes(keyword))) {
    return { type: BleConnectionErrorType.BLUETOOTH_OFF, messageKey: 'ble.connection.errors.bluetoothOff', isRetryable: true };
  }

  const locationKeywords = ['location services are required', 'location is disabled', 'location services disabled'];
  if (locationKeywords.some(keyword => lowerError.includes(keyword))) {
    return { type: BleConnectionErrorType.LOCATION_DISABLED, messageKey: 'ble.connection.errors.locationDisabled', isRetryable: true };
  }

  // iOS surfaces revoked/undetermined BT authorization as "bluetooth state unauthorized" — permission, not BT-off.
  const permissionKeywords = [
    'not authorized',
    'unauthorized',
    'bluetooth state unauthorized',
    'bluetooth permission',
    'permission denied',
    'user denied',
    'location permission',
    'bluetooth_scan',
    'bluetooth_connect',
    'requires permission',
    'missing permission',
  ];
  if (permissionKeywords.some(keyword => lowerError.includes(keyword))) {
    return { type: BleConnectionErrorType.BLE_PERMISSION_DENIED, messageKey: 'ble.connection.errors.permissionDenied', isRetryable: false };
  }

  const timeoutKeywords = ['timeout', 'timed out', 'did not respond', 'no response'];
  if (timeoutKeywords.some(keyword => lowerError.includes(keyword))) {
    return { type: BleConnectionErrorType.TIMEOUT, messageKey: 'ble.connection.errors.timeout', isRetryable: true };
  }

  // Word-scoped matches so generic strings like "request rejected by device" or "operation cancelled"
  // (which aren't user-initiated) don't get misclassified as USER_CANCELED.
  const cancelKeywords = ['user cancel', 'user-cancel', 'user declined', 'user rejected', 'pairing cancel', 'pairing aborted'];
  if (cancelKeywords.some(keyword => lowerError.includes(keyword))) {
    return { type: BleConnectionErrorType.USER_CANCELED, messageKey: 'ble.connection.errors.userCanceled', isRetryable: true };
  }

  const alreadyConnectedKeywords = ['already connected', 'already in use', 'device busy'];
  if (alreadyConnectedKeywords.some(keyword => lowerError.includes(keyword))) {
    return { type: BleConnectionErrorType.DEVICE_ALREADY_CONNECTED, messageKey: 'ble.connection.errors.alreadyConnected', isRetryable: false };
  }

  const disconnectKeywords = ['disconnected immediately', 'connection lost', 'link loss'];
  if (disconnectKeywords.some(keyword => lowerError.includes(keyword))) {
    return { type: BleConnectionErrorType.DEVICE_DISCONNECTED, messageKey: 'ble.connection.errors.deviceDisconnected', isRetryable: true };
  }

  return {
    type: BleConnectionErrorType.UNKNOWN,
    messageKey: 'ble.connection.errors.unknown',
    messageParams: { error: errorString },
    isRetryable: true,
  };
}
